import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { useCallback, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import {
  DELETE_DOCUMENT_MANIFEST,
  PROCESSED_MANIFESTS_QUERY,
} from "routes/Customer/Waste/Manifests/api";

import { DOCUMENT_MANIFEST_DETAIL, EDIT_DOCUMENT_MANIFEST } from "../queries";

type ManifestFormValues = {
  manifestTrackingNumber: string;
  shippedDate: string;
  receivedDate: string;
  generatorId: string;
  generatorName: string;
  generatorMailStreet1: string;
  generatorMailStreet2: string;
  generatorMailCity: string;
  generatorMailState: string;
  generatorMailZip: string;
  generatorLocationStreet1: string;
  generatorLocationStreet2: string;
  generatorLocationCity: string;
  generatorLocationState: string;
  generatorLocationZip: string;
  desFacilityId: string;
  desFacilityName: string;
  desFacLocationStreet1: string;
  desFacLocationCity: string;
  desFacLocationState: string;
  desFacLocationZip: string;
  wasteLines: {
    wasteLineNumber: string;
    dotHazardous: boolean;
    dotIdNumberDescription: string;
    dotPrintedInformation: string;
    nonHazWasteDescription: string;
    containerNumber: number | null;
    containerTypeCode: string;
    quantity: number | null;
    qtyUnitOfMeasureCode: string;
    managementMethodCode: string;
    federalWasteCodes: string;
    stateWasteCodes: string;
  }[];
  transporters: {
    transporterLineNumber: string;
    transporterId: string;
    transporterName: string;
  }[];
};

function formatDateForInput(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

export function useManifestReviewSection({
  canShowManifestReview,
  wasteManifestId: wasteManifestIdProp,
  documentId,
  onClose,
}: {
  canShowManifestReview: boolean;
  /** Resolved waste manifest ID — from the dialog prop or the document's sourceWasteManifestId */
  wasteManifestId?: string | null;
  /** Current document ID — needed for document deletion */
  documentId?: string;
  /** Close the parent dialog — called after detach or delete */
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const { queueManifestDocumentExtraction } = useQueueManifestDocumentExtraction();
  const [deleteDocumentMutation, { loading: deletingDocument }] = useDeleteDocumentMutation({
    refetchQueries: [PROCESSED_MANIFESTS_QUERY, "Documents", "FacilityDocuments"],
  });

  const wasteManifestId = wasteManifestIdProp ?? null;

  const { data: manifestDetailData, loading: manifestDetailLoading } = useQuery(
    DOCUMENT_MANIFEST_DETAIL,
    {
      variables: { wasteManifestId: wasteManifestId ?? "" },
      skip: !wasteManifestId || !canShowManifestReview,
      fetchPolicy: "cache-and-network",
    }
  );

  const [editManifestMutation, { loading: manifestSaving }] = useMutation(EDIT_DOCUMENT_MANIFEST, {
    refetchQueries: ["ProcessedManifests", "DocumentManifestDetailForReview"],
  });

  const [deleteManifestMutation, { loading: deletingManifest }] = useMutation(
    DELETE_DOCUMENT_MANIFEST,
    { refetchQueries: ["ProcessedManifests"] }
  );

  // Map manifest detail data to form values
  const manifestFormValues = useMemo((): ManifestFormValues => {
    const detail = manifestDetailData?.documentManifestDetail;
    const wm = detail?.wasteManifest;
    const wasteLines = detail?.wasteLines ?? [];
    const wasteTransporters = detail?.wasteTransporters ?? [];

    return {
      manifestTrackingNumber: wm?.manifestTrackingNumber ?? "",
      shippedDate: formatDateForInput(wm?.shippedDate),
      receivedDate: formatDateForInput(wm?.receivedDate),
      generatorId: wm?.generatorId ?? "",
      generatorName: wm?.generatorName ?? "",
      generatorMailStreet1: wm?.generatorMailStreet1 ?? "",
      generatorMailStreet2: wm?.generatorMailStreet2 ?? "",
      generatorMailCity: wm?.generatorMailCity ?? "",
      generatorMailState: wm?.generatorMailState ?? "",
      generatorMailZip: wm?.generatorMailZip ?? "",
      generatorLocationStreet1: wm?.generatorLocationStreet1 ?? "",
      generatorLocationStreet2: wm?.generatorLocationStreet2 ?? "",
      generatorLocationCity: wm?.generatorLocationCity ?? "",
      generatorLocationState: wm?.generatorLocationState ?? "",
      generatorLocationZip: wm?.generatorLocationZip ?? "",
      desFacilityId: wm?.desFacilityId ?? "",
      desFacilityName: wm?.desFacilityName ?? "",
      desFacLocationStreet1: wm?.desFacLocationStreet1 ?? "",
      desFacLocationCity: wm?.desFacLocationCity ?? "",
      desFacLocationState: wm?.desFacLocationState ?? "",
      desFacLocationZip: wm?.desFacLocationZip ?? "",
      wasteLines: wasteLines.map((wl) => ({
        wasteLineNumber: wl.wasteLineNumber,
        dotHazardous: wl.dotHazardous ?? false,
        dotIdNumberDescription: wl.dotIdNumberDescription ?? "",
        dotPrintedInformation: wl.dotPrintedInformation ?? "",
        nonHazWasteDescription: wl.nonHazWasteDescription ?? "",
        containerNumber: wl.containerNumber ?? null,
        containerTypeCode: wl.containerTypeCode ?? "",
        quantity: wl.quantity ?? null,
        qtyUnitOfMeasureCode: wl.qtyUnitOfMeasureCode ?? "",
        managementMethodCode: wl.managementMethodCode ?? "",
        federalWasteCodes: (wl.federalWasteCodes ?? []).join(", "),
        stateWasteCodes: (wl.stateWasteCodes ?? []).join(", "),
      })),
      transporters: wasteTransporters.map((t) => ({
        transporterLineNumber: t.transporterLineNumber,
        transporterId: t.transporterId ?? "",
        transporterName: t.transporterName ?? "",
      })),
    };
  }, [manifestDetailData]);

  const manifestForm = useForm<ManifestFormValues>({ values: manifestFormValues });
  const {
    control: manifestControl,
    handleSubmit: handleManifestSubmit,
    watch,
    getValues,
    formState: { isDirty: manifestIsDirty },
  } = manifestForm;

  const manifestNeedsReview = useMemo(
    () =>
      manifestDetailData?.documentManifestDetail?.wasteManifest?.sourceDocument?.documentMetadata
        ?.needsReview === true,
    [manifestDetailData]
  );

  const originalTrackingNumber =
    manifestDetailData?.documentManifestDetail?.wasteManifest?.manifestTrackingNumber ?? "";
  const currentTrackingNumber = watch("manifestTrackingNumber");
  const trackingNumberChanged =
    !!originalTrackingNumber &&
    !!currentTrackingNumber &&
    currentTrackingNumber !== originalTrackingNumber;

  const wasteLineFieldArray = useFieldArray({ control: manifestControl, name: "wasteLines" });
  const transporterFieldArray = useFieldArray({
    control: manifestControl,
    name: "transporters",
  });

  const [deletedWasteLineNumbers, setDeletedWasteLineNumbers] = useState<string[]>([]);
  const [deletedTransporterLineNumbers, setDeletedTransporterLineNumbers] = useState<string[]>([]);

  // Queue manifest extraction for Scenario B (user classified as manifest, no data exists)
  const queueExtractionIfNeeded = useCallback(
    async (docId: string, tenantId: string) => {
      if (!canShowManifestReview) return;
      await queueManifestDocumentExtraction({
        tenantId,
        documentIds: [docId],
      });
    },
    [canShowManifestReview, queueManifestDocumentExtraction]
  );

  // Save manifest form data
  const saveManifest = useCallback(async () => {
    if (!wasteManifestId || (!manifestIsDirty && !manifestNeedsReview)) return;

    return new Promise<void>((resolve, reject) => {
      handleManifestSubmit(async (values) => {
        try {
          await editManifestMutation({
            variables: {
              wasteManifestId,
              input: {
                manifestTrackingNumber: values.manifestTrackingNumber || null,
                shippedDate: values.shippedDate || null,
                receivedDate: values.receivedDate || null,
                generatorId: values.generatorId || null,
                generatorName: values.generatorName || null,
                generatorMailStreet1: values.generatorMailStreet1 || null,
                generatorMailStreet2: values.generatorMailStreet2 || null,
                generatorMailCity: values.generatorMailCity || null,
                generatorMailState: values.generatorMailState || null,
                generatorMailZip: values.generatorMailZip || null,
                generatorLocationStreet1: values.generatorLocationStreet1 || null,
                generatorLocationStreet2: values.generatorLocationStreet2 || null,
                generatorLocationCity: values.generatorLocationCity || null,
                generatorLocationState: values.generatorLocationState || null,
                generatorLocationZip: values.generatorLocationZip || null,
                desFacilityId: values.desFacilityId || null,
                desFacilityName: values.desFacilityName || null,
                desFacLocationStreet1: values.desFacLocationStreet1 || null,
                desFacLocationCity: values.desFacLocationCity || null,
                desFacLocationState: values.desFacLocationState || null,
                desFacLocationZip: values.desFacLocationZip || null,
                wasteLines: values.wasteLines.map((wl) => ({
                  wasteLineNumber: wl.wasteLineNumber,
                  dotHazardous: wl.dotHazardous,
                  dotIdNumberDescription: wl.dotIdNumberDescription || null,
                  dotPrintedInformation: wl.dotPrintedInformation || null,
                  nonHazWasteDescription: wl.nonHazWasteDescription || null,
                  containerNumber: wl.containerNumber,
                  containerTypeCode: wl.containerTypeCode || null,
                  quantity: wl.quantity,
                  qtyUnitOfMeasureCode: wl.qtyUnitOfMeasureCode || null,
                  managementMethodCode: wl.managementMethodCode || null,
                  federalWasteCodes: wl.federalWasteCodes
                    ? wl.federalWasteCodes
                        .split(",")
                        .map((c) => c.trim())
                        .filter(Boolean)
                    : [],
                  stateWasteCodes: wl.stateWasteCodes
                    ? wl.stateWasteCodes
                        .split(",")
                        .map((c) => c.trim())
                        .filter(Boolean)
                    : [],
                })),
                transporters: values.transporters.map((t) => ({
                  transporterLineNumber: t.transporterLineNumber,
                  transporterId: t.transporterId || null,
                  transporterName: t.transporterName || null,
                })),
                deleteWasteLineNumbers: deletedWasteLineNumbers,
                deleteTransporterLineNumbers: deletedTransporterLineNumbers,
                markReviewed: manifestNeedsReview,
              },
            },
          });
          setDeletedWasteLineNumbers([]);
          setDeletedTransporterLineNumbers([]);
          resolve();
        } catch (err) {
          reject(err);
        }
      })();
    });
  }, [
    wasteManifestId,
    manifestIsDirty,
    handleManifestSubmit,
    editManifestMutation,
    deletedWasteLineNumbers,
    deletedTransporterLineNumbers,
    manifestNeedsReview,
  ]);

  const manifestLoading = manifestDetailLoading;
  const manifestDataLoaded = !!manifestDetailData?.documentManifestDetail && !manifestDetailLoading;
  const hasManifestData = !!wasteManifestId;
  const manifestFacilityId =
    manifestDetailData?.documentManifestDetail?.wasteManifest?.facility?.id ?? undefined;

  // Re-extract: queue extraction with current MTN + generatorId as overrides
  const reExtractDocument = useCallback(
    async (docId: string, tenantId: string) => {
      const currentMTN = getValues("manifestTrackingNumber") || undefined;
      const currentGeneratorId = getValues("generatorId") || undefined;
      return queueManifestDocumentExtraction({
        tenantId,
        documentIds: [docId],
        facilityId: manifestFacilityId,
        manifestTrackingNumber: currentMTN,
        generatorId: currentGeneratorId,
      });
    },
    [getValues, manifestFacilityId, queueManifestDocumentExtraction]
  );

  // Detach: delete manifest records, keep source document
  const detachManifestData = useCallback(async () => {
    if (!wasteManifestId) return;
    await deleteManifestMutation({ variables: { wasteManifestId } });
  }, [wasteManifestId, deleteManifestMutation]);

  // Detach and close dialog, refreshing the manifests list
  const handleDetached = useCallback(async () => {
    await detachManifestData();
    client.refetchQueries({ include: [PROCESSED_MANIFESTS_QUERY] });
    onClose();
  }, [detachManifestData, client, onClose]);

  // Delete the document and all associated manifest data, then close.
  // Note: detach must run first due to the RESTRICT FK on WasteManifest.sourceDocumentId.
  // If deleteDocument fails after detach succeeds, the manifest data is lost but the
  // document remains
  const handleDeleteDocument = useCallback(async () => {
    if (!documentId) return;
    await detachManifestData();
    await deleteDocumentMutation({
      variables: { id: documentId, retainAsset: false },
    });
    alerts.success("Document and associated manifest data deleted.");
    client.refetchQueries({ include: [PROCESSED_MANIFESTS_QUERY] });
    onClose();
  }, [documentId, detachManifestData, deleteDocumentMutation, alerts, client, onClose]);

  return {
    manifestDataLoaded,
    manifestLoading,
    manifestSaving,
    deletingManifest,
    manifestNeedsReview,
    hasManifestData,
    trackingNumberChanged,
    manifestControl,
    manifestWatch: watch,
    wasteLineFieldArray,
    transporterFieldArray,
    onDeleteWasteLine: (lineNumber: string) =>
      setDeletedWasteLineNumbers((prev) => [...prev, lineNumber]),
    onDeleteTransporter: (lineNumber: string) =>
      setDeletedTransporterLineNumbers((prev) => [...prev, lineNumber]),
    manifestIsDirty,
    wasteManifestId,
    deletingDocument,
    queueExtractionIfNeeded,
    reExtractDocument,
    detachManifestData,
    handleDetached,
    handleDeleteDocument,
    saveManifest,
    onClose,
  };
}
